/**
 * A session desk with a full-height assistant or transcript beside it.
 * Phone navigation selects one destination; desktop keeps the board visible.
 * Both side panels stay mounted so switching preserves an unsent message and
 * the transcript's reading position. Names have one shared disclosure, avoiding
 * duplicate requests from separate mobile and desktop instances.
 */
import { useState, type ReactNode } from 'react';

import type { ChatMessage, ChatMode, TranscriptTurn } from '@/api/types';
import type { GlanceEntity, PoolName } from '@/api/client';
import { NamesPanel } from '@/components/NamesPanel';
import { ChatPanel } from '@/components/ChatPanel';
import { TranscriptPane } from '@/components/TranscriptPane';

export type LiveView = 'board' | 'heard' | 'chat';

interface Props {
  view: LiveView;
  onView: (view: LiveView) => void;
  /** Everything above the storyboard: the encounter cue, narration, the board
   *  itself. Passed as children because App owns the combat lifecycle. */
  board: ReactNode;
  campaignName?: string;
  wideBoard?: boolean;
  glance: GlanceEntity[];
  /** Needed by the names panel, which is per-campaign rather than per-session. */
  campaignId: string | null;
  /** Local names used by the Tutorial's no-network session rehearsal. */
  namePreviewPool?: PoolName[];
  transcript: TranscriptTurn[];
  chat: ChatMessage[];
  chatDisabled: boolean;
  chatPresentation?: { mode: ChatMode; value: string; sending?: boolean };
  onSend: (mode: ChatMode, content: string) => Promise<void>;
  /** Unread markers for the tabs a GM is not looking at. Without these, a phone
   *  GM has no way to know the table said something while they were on Board. */
  unreadHeard: number;
  unreadChat: number;
  unreadBoard?: number;
}

export function LiveWorkspace({
  view,
  onView,
  board,
  campaignName,
  wideBoard = false,
  glance,
  campaignId,
  namePreviewPool,
  transcript,
  chat,
  chatDisabled,
  chatPresentation,
  onSend,
  unreadHeard,
  unreadChat,
  unreadBoard = 0,
}: Props) {
  const [showAssistant, setShowAssistant] = useState(false);
  const [sideView, setSideView] = useState<'heard' | 'chat'>('chat');
  const activeSide = view === 'board' ? sideView : view;
  function selectSide(next: 'heard' | 'chat') {
    setSideView(next);
    onView(next);
  }
  return (
    <>
      <main
        tabIndex={-1}
        className="workspace workspace--live"
        data-view={view}
        data-side={activeSide}
        data-focus={wideBoard && !showAssistant}
      >
        <section className="pane" data-pane="board">
          <header className="pane__header">
            <div>
              <p className="desk-eyebrow">Your session desk</p>
              <h1 className="desk-title">
                {campaignName || 'The story so far'}
              </h1>
            </div>
            <span className="pane__spacer" />
            <span className="privacy-label">GM only</span>
            {wideBoard ? (
              <button
                type="button"
                className="button button--small desk-focus"
                aria-pressed={showAssistant}
                onClick={() => setShowAssistant((value) => !value)}
              >
                {showAssistant ? 'Focus map' : 'Show assistant'}
              </button>
            ) : null}
          </header>
          <div className="pane__body">
            <details className="board__canon">
              <summary className="board__canon-open">
                Names
                {glance.length > 0 ? (
                  <span className="board__canon-count">{glance.length}</span>
                ) : null}
              </summary>
              <NamesPanel
                campaignId={campaignId}
                entities={glance}
                previewPool={namePreviewPool}
              />
            </details>
            {board}
          </div>
        </section>

        <aside className="sidebar">
          <div
            className="desk-tools"
            role="group"
            aria-label="Session assistant"
          >
            <button
              type="button"
              aria-pressed={activeSide === 'chat'}
              onClick={() => selectSide('chat')}
            >
              Assistant{' '}
              {unreadChat > 0 && activeSide !== 'chat' ? (
                <span className="desk-count">{unreadChat}</span>
              ) : null}
            </button>
            <button
              type="button"
              aria-pressed={activeSide === 'heard'}
              onClick={() => selectSide('heard')}
            >
              Transcript{' '}
              {unreadHeard > 0 && activeSide !== 'heard' ? (
                <span className="desk-count">{unreadHeard}</span>
              ) : null}
            </button>
          </div>
          <div className="pane-wrap" data-pane="heard">
            <TranscriptPane turns={transcript} presentation={!!chatPresentation} />
          </div>
          <div className="pane-wrap" data-pane="chat">
            <ChatPanel
              presentation={chatPresentation}
              messages={chat}
              disabled={chatDisabled}
              onSend={onSend}
            />
          </div>
        </aside>
      </main>

      {/* Bottom bar, not top: a thumb reaches the bottom of a phone and not the
          top, and the masthead is already carrying the mic control. Hidden on
          desktop, where every pane is on screen and there is nothing to switch
          between. */}
      <nav className="livetabs" aria-label="Session views">
        <TabButton
          active={view === 'board'}
          onClick={() => onView('board')}
          label="Board"
          badge={unreadBoard}
        />
        <TabButton
          active={view === 'heard'}
          onClick={() => onView('heard')}
          label="Transcript"
          badge={unreadHeard}
        />
        <TabButton
          active={view === 'chat'}
          onClick={() => onView('chat')}
          label="Assistant"
          badge={unreadChat}
        />
      </nav>
    </>
  );
}

function TabButton({
  active,
  onClick,
  label,
  badge = 0,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  badge?: number;
}) {
  return (
    <button
      type="button"
      className={active ? 'livetabs__tab livetabs__tab--on' : 'livetabs__tab'}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
    >
      {label}
      {badge > 0 && !active ? (
        <span className="livetabs__badge" aria-label={`${badge} new`}>
          {badge > 9 ? '9+' : badge}
        </span>
      ) : null}
    </button>
  );
}
